import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useVoiceInput — Web Speech API dictation.
 *
 * Earlier versions of this hook ran TWO concurrent audio captures: one
 * via `navigator.mediaDevices.getUserMedia` (for a Web Audio level
 * meter that drove a real-amplitude waveform) and one inside the
 * browser's `SpeechRecognition` (which has its own private audio
 * capture). Chrome doesn't reliably permit that combo — the two streams
 * fought each other, the mic indicator flashed on/off as one or the
 * other repeatedly grabbed and released the device, and
 * `SpeechRecognition.start()` would sometimes fire `service-not-allowed`
 * even with permission granted. Net effect: tapping the mic looked
 * "weird and didn't actually record."
 *
 * Now: SpeechRecognition only. The waveform component animates from
 * an `isSpeaking` boolean derived from interim-transcript activity.
 * Not a real RMS level, but visually very close — bars surge when the
 * recogniser is committing partial text and subside otherwise. Single
 * audio capture, single permission grant, no race.
 */

type VoiceError = 'permission-denied' | 'no-mic' | 'no-speech' | 'aborted' | 'generic';

interface SpeechRecognitionLike {
  start: () => void;
  stop: () => void;
  abort: () => void;
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((e: any) => void) | null;
  onend: ((e: any) => void) | null;
  onerror: ((e: any) => void) | null;
  onstart: ((e: any) => void) | null;
}
type SpeechCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechCtor;
    webkitSpeechRecognition?: SpeechCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface UseVoiceInputResult {
  supported: boolean;
  isListening: boolean;
  /** Current partial transcript (not yet committed). Empty between turns. */
  interim: string;
  /**
   * True for ~600ms after the recogniser commits any new interim text.
   * Use to drive a visual "speaking" affordance in the waveform.
   * Not a real audio level — we don't tap the mic separately.
   */
  isSpeaking: boolean;
  /** Non-null while an error is being surfaced; null otherwise. */
  error: VoiceError | null;
  /** Toggle listening on/off. Idempotent. */
  toggle: () => void;
  /** Stop without committing anything pending. */
  stop: () => void;
}

const SPEAKING_DECAY_MS = 600;

/**
 * @param onCommit Called with the FINALISED transcript chunks. Concat
 *                  inside the caller so multiple commits in a single
 *                  session accumulate into the textarea.
 */
export function useVoiceInput(
  onCommit: (text: string) => void,
): UseVoiceInputResult {
  const [isListening, setIsListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<VoiceError | null>(null);

  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const userStoppedRef = useRef(false);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  // Restart bookkeeping. SpeechRecognition ends itself on silence; we
  // restart to keep the session open. Hard-cap restarts so a flapping
  // device or a flaky service can't lock the CPU into a tight loop.
  const restartTimestampsRef = useRef<number[]>([]);
  const restartTimeoutRef = useRef<number | null>(null);
  // Drives the cosmetic waveform — decays back to idle after the last
  // interim-text update.
  const speakingTimeoutRef = useRef<number | null>(null);

  const supported = getSpeechRecognitionCtor() !== null;

  const teardown = useCallback(() => {
    if (restartTimeoutRef.current !== null) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }
    if (speakingTimeoutRef.current !== null) {
      clearTimeout(speakingTimeoutRef.current);
      speakingTimeoutRef.current = null;
    }
    restartTimestampsRef.current = [];
    try {
      recRef.current?.abort();
    } catch {
      // already dead — fine
    }
    recRef.current = null;
    setIsSpeaking(false);
    setInterim('');
  }, []);

  const markSpoke = useCallback(() => {
    setIsSpeaking(true);
    if (speakingTimeoutRef.current !== null) {
      clearTimeout(speakingTimeoutRef.current);
    }
    speakingTimeoutRef.current = window.setTimeout(() => {
      speakingTimeoutRef.current = null;
      setIsSpeaking(false);
    }, SPEAKING_DECAY_MS);
  }, []);

  const start = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setError('generic');
      return;
    }
    userStoppedRef.current = false;
    setError(null);

    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language?.startsWith('fr') ? 'fr-FR' : 'en-US';

    rec.onresult = (e: any) => {
      // Walk results from event.resultIndex onward (the new ones).
      let finalChunk = '';
      let interimChunk = '';
      for (let i = e.resultIndex ?? 0; i < e.results.length; i += 1) {
        const r = e.results[i];
        const text = r[0]?.transcript ?? '';
        if (r.isFinal) finalChunk += text;
        else interimChunk += text;
      }
      if (finalChunk) {
        onCommitRef.current(finalChunk.trim());
        setInterim('');
      } else {
        setInterim(interimChunk);
      }
      // Any new chunk — interim OR final — means the user is currently
      // speaking. Drive the cosmetic waveform off this.
      markSpoke();
    };

    rec.onerror = (e: any) => {
      const code = e?.error;
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        setError('permission-denied');
        userStoppedRef.current = true;
      } else if (code === 'no-speech') {
        // Silence timeout — auto-restart picks it up. Don't surface.
      } else if (code === 'aborted') {
        // User pressed stop or we tore down — fine.
      } else if (code === 'audio-capture') {
        // Mic disappeared mid-session (USB unplug, mac sleep). Terminal.
        setError('no-mic');
        userStoppedRef.current = true;
      } else if (code === 'network') {
        // Recogniser can't reach the speech service. Terminal — looping
        // would just spin.
        setError('generic');
        userStoppedRef.current = true;
      } else if (code === 'language-not-supported' || code === 'bad-grammar') {
        setError('generic');
        userStoppedRef.current = true;
      } else {
        setError('generic');
      }
    };

    // Cap auto-restarts: 5 within 3 seconds → terminate. Stops device-
    // flapping / permission-revoked-mid-session loops from pegging CPU.
    const RESTART_WINDOW_MS = 3000;
    const MAX_RESTARTS_IN_WINDOW = 5;

    rec.onend = () => {
      if (userStoppedRef.current) {
        teardown();
        setIsListening(false);
        return;
      }
      const now = performance.now();
      restartTimestampsRef.current = restartTimestampsRef.current.filter(
        (t) => now - t < RESTART_WINDOW_MS,
      );
      if (restartTimestampsRef.current.length >= MAX_RESTARTS_IN_WINDOW) {
        setError('generic');
        userStoppedRef.current = true;
        teardown();
        setIsListening(false);
        return;
      }
      restartTimestampsRef.current.push(now);

      // 50ms latch so Chromium has time to release the previous session
      // before we start a new one — without this, `start()` sometimes
      // throws `InvalidStateError: recognition has already started`.
      restartTimeoutRef.current = window.setTimeout(() => {
        restartTimeoutRef.current = null;
        if (userStoppedRef.current) return;
        try {
          rec.start();
        } catch {
          teardown();
          setIsListening(false);
        }
      }, 50);
    };

    recRef.current = rec;
    try {
      rec.start();
      setIsListening(true);
    } catch (err: any) {
      // `start()` throws synchronously when the recogniser is already
      // running (very rare), or when the browser/page can't access the
      // mic. Surface a useful error and bail.
      if (err?.name === 'NotAllowedError') {
        setError('permission-denied');
      } else {
        setError('generic');
      }
      teardown();
      setIsListening(false);
    }
  }, [markSpoke, teardown]);

  const stop = useCallback(() => {
    userStoppedRef.current = true;
    try {
      recRef.current?.stop();
    } catch {
      // ignore
    }
    teardown();
    setIsListening(false);
  }, [teardown]);

  const toggle = useCallback(() => {
    if (isListening) stop();
    else start();
  }, [isListening, start, stop]);

  // Tear down on unmount — releases the mic indicator immediately.
  useEffect(() => teardown, [teardown]);

  return { supported, isListening, interim, isSpeaking, error, toggle, stop };
}
